import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/store';
import { subscriptionApi } from './api';
import { subscriptionKeys } from './queryKeys';
import type { SubscriptionResponse, PlanInfo } from './types';

/**
 * Single source of truth for subscription state, plans, and mutations.
 * Account-level (not per-restaurant).
 */
export function useSubscription() {
  const token = useAuthStore((s) => s.token);

  const { data: subscription, isLoading, refetch } = useQuery<SubscriptionResponse>({
    queryKey: subscriptionKeys.all,
    queryFn: () => subscriptionApi.getSubscription(token),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });

  const { data: plans = [], isLoading: plansLoading } = useQuery<PlanInfo[]>({
    queryKey: subscriptionKeys.plans,
    queryFn: () => subscriptionApi.getPlans(),
    staleTime: 10 * 60 * 1000,
  });

  const openPortal = useMutation({
    mutationFn: () => subscriptionApi.createPortalSession(token),
    onSuccess: (data) => {
      if (data?.url) window.location.href = data.url;
    },
  });

  const status = subscription?.subscriptionStatus ?? 'NONE';
  const isPending = status === 'PENDING';
  const hasStripeSubscription = !!subscription?.stripeSubscriptionId;

  return {
    subscription,
    plans,
    isLoading,
    plansLoading,
    refetch,

    plan: subscription?.subscriptionPlan ?? 'NONE',
    status,
    isActive: status === 'ACTIVE',
    isPending,
    isPastDue: status === 'PAST_DUE',
    isCancelled: status === 'CANCELLED',
    isNone: status === 'NONE',
    canSubscribe: status === 'NONE' || status === 'CANCELLED',
    pendingActivation: subscription?.pendingActivation ?? false,
    /** PENDING + no Stripe subscription = user never completed the checkout form. */
    isCheckoutIncomplete: isPending && !hasStripeSubscription,
    /** PENDING + Stripe subscription exists = payment captured, webhook activation in progress. */
    isActivating: isPending && hasStripeSubscription,

    currentPeriodEnd: subscription?.subscriptionCurrentPeriodEnd ?? null,

    isTrialing: subscription?.trialing ?? false,
    trialEnd: subscription?.trialEnd ?? null,
    trialDaysLeft: subscription?.trialEnd
      ? Math.max(0, Math.ceil((new Date(subscription.trialEnd).getTime() - Date.now()) / 86_400_000))
      : null,
    isTrialEligible: subscription?.trialEligible ?? true,

    canShowMenu: subscription?.canShowMenu ?? false,
    canManageReservations: subscription?.canManageReservations ?? false,
    canAcceptOrders: subscription?.canAcceptOrders ?? false,
    canAcceptOnlinePayment: subscription?.canAcceptOnlinePayment ?? false,
    canUseDiscounts: subscription?.canUseDiscounts ?? false,
    canUsePrinter: subscription?.canUsePrinter ?? false,

    openPortal,
  };
}
